import React from "react";
import PropTypes from "prop-types";
import { compose } from "redux";
import { connect } from "react-redux";
import { useHistory, withRouter } from "react-router";
import classnames from "classnames";
import moment from "moment/moment";
import * as styles from "./ProviderClinicTable.scss"
import get from "lodash/get";
import find from "lodash/find";
import map from "lodash/map";
import toInteger from "lodash/toInteger";
import Button from "components/common/Button";
import Image from "components/common/Image";
import SortableDataTable from "components/common/SortableDataTable";
import * as ClinicAdminLinks from "utils/ClinicAdminLinks";
import * as ClinicProviderLinks from "utils/ClinicProviderLinks";
import { formatUSD } from "utils/numeric";
import * as ProviderLinks from "utils/ProviderLinks";
import { PermissionTypes, userHasPermission } from "utils/permissions/rolesPermissions";
import toast from "utils/toast";
import * as OrgNodeTypes from "constants/OrgNodeTypes";
import { BOEHRINGER_INGELHEIM, PREMIER_PET_CARE_PLAN } from "constants/ProviderIds";
import * as UserPermissions from "constants/UserPermissions";

function ProviderClinicTable({
    canViewClinicBIGamesDashboard = false,
    canViewClinicInfo = false,
    canViewClinicPrograms = false,
    canViewCouponLibrary = false,
    canViewCorporateGroupClinic = false,
    canViewGamesDashboard = false,
    canViewRedemptionSummary = false,
    columns,
    downloadPDF,
    isAdmin = false,
    nodeId,
    nodeType,
    onRedemptionsIconClicked,
    onRowClick,
    onRowHover,
    providerBIEnrollments,
    providerId,
    providers,
    rawData,
    resultsPerPage = 10,
    search,
    showAllColumns = true,
    title,
    wellnessOnly = false,
}) {
    const history = useHistory();

    const handleClick = (e, data) => {
        e.stopPropagation();
        e.preventDefault();
        onRedemptionsIconClicked(data);
    }
    const handleGoToClinicGames = (e, clinicId, providerId) => {
        e.stopPropagation();
        e.preventDefault();
        if ((canViewClinicInfo || canViewCorporateGroupClinic) && canViewGamesDashboard) {
            if (isAdmin) {
                history.push(ClinicAdminLinks.getLinkLocation("games", clinicId, providerId));
            } else {
                history.push(ClinicProviderLinks.getLinkLocation("games", nodeId, clinicId, providerId));
            }
        } else if (canViewGamesDashboard) {
            history.push(ProviderLinks.getLinkLocation("clinic-games", {
                providerId,
                nodeId,
                isAdmin,
                clinicId
            }));
        } else {
            toast.error("You do not have the correct permissions to view clinic games.");
        }
    }

    const handleGoToClinicPrograms = (e, clinicId, providerId) => {
        e.stopPropagation();
        e.preventDefault();
        if ((canViewClinicInfo || canViewCorporateGroupClinic) && canViewClinicPrograms) {
            if (isAdmin) {
                history.push(ClinicAdminLinks.getLinkLocation("programs", clinicId, providerId));
            } else {
                history.push(ClinicProviderLinks.getLinkLocation("programs", nodeId, clinicId, providerId));
            }
        } else if (canViewClinicPrograms) {
            history.push(ProviderLinks.getLinkLocation("clinic-programs", {
                providerId,
                nodeId,
                isAdmin,
                clinicId
            }));
        } else {
            toast.error("You do not have the correct permissions to view clinic programs.");
        }
    }

    const handleGoToCoupons = (e, clinicId, providerId) => {
        e.stopPropagation();
        e.preventDefault();
        if (canViewClinicInfo || canViewCorporateGroupClinic) {
            if (isAdmin) {
                history.push(ClinicAdminLinks.getLinkLocation("libraryProgram", clinicId, providerId));
            } else {
                history.push(ClinicProviderLinks.getLinkLocation("libraryProgram", nodeId, clinicId, providerId));
            }
        } else {
            history.push(ProviderLinks.getLinkLocation("clinic-coupon-library", {
                providerId,
                nodeId,
                isAdmin,
                clinicId
            }));
        }
    }

    const getManufacturerColumn = (provider) => {
        return {
            topHeader: (
                <div className="text-center">
                    <Image
                        alt={provider.name}
                        src={provider.smallImageUri}
                        style={{ borderRadius: "5px" }}
                        title={provider.name}
                    />
                </div>
            ),
            name: (<div className="text-center">{provider.name}</div>),
            selector: `providerClinicEnrollments-${provider.id}`,
            key: `providerClinicEnrollments-${provider.id}`,
            sortable: true,
            sortValue: row => {
                const p = find(row.providerClinicEnrollments, { providerId: provider.id });
                if (!!p) {
                    if (!!p.couponDeactivationDate) {
                        return 2;
                    } else if (!!p.couponActivationDate) {
                        return 0;
                    } else if (!!p.enrollmentDate) {
                        return 1;
                    }
                }
                return 3
            },
            format: row => {
                const p = find(row.providerClinicEnrollments, { providerId: provider.id });
                if (!!p) {
                    if (!!p.couponDeactivationDate) {
                        return (
                            <div
                                className="text-center text-warn"
                                title={`Coupon Deactivation: ${moment(p.couponDeactivationDate).format("MM/DD/YYYY LTS")}`}
                            >
                                Ended
                            </div>
                        );
                    } else if (!!p.couponActivationDate) {
                        return (
                            <div
                                className="text-center"
                                title={`Coupon Activation: ${moment(p.couponActivationDate).format("MM/DD/YYYY LTS")}`}
                            >
                                <i className="fa fa-check-circle fa-2x text-success"/>
                            </div>
                        );
                    } else if (!!p.enrollmentDate) {
                        return (
                            <div
                                className="text-center"
                                title={`Enrollment: ${moment(p.enrollmentDate).format("MM/DD/YYYY LTS")}`}
                            >
                                Registered
                            </div>
                        );
                    }
                }
                return <div/>;
            },
            total: true,
            totalClasses: "text-center",
            rowTotal: row => {
                const p = find(row.providerClinicEnrollments, { providerId: provider.id });
                if (!!p) {
                    if (!!p.couponDeactivationDate) {
                        return 0;
                    } else if (!!p.couponActivationDate) {
                        return 1;
                    }
                }
                return 0;
            }
        }
    }

    const getIRManufacturerColumn = (provider) => {
        return {
            topHeader: (<div/>),
            name: (
                <div className="text-center">
                    {provider.shortName} Instant Rebates
                </div>
            ),
            selector: `providerClinicEnrollments-${provider.id}-InstantRebates`,
            key: `providerClinicEnrollments-${provider.id}-InstantRebates`,
            sortable: true,
            sortValue: row => {
                // Special ordering for the instant rebate column
                const p = find(row.providerClinicEnrollments, {providerId: provider.id});
                return !p.isIr;
            },
            format: row => {
                // Special checkbox for the instant rebate column
                const p = find(row.providerClinicEnrollments, {providerId: provider.id});
                if (p.isIr) {
                    return (
                        <div
                            className="text-center"
                            title={`Instant Rebate Activation: ${!!p.irDate ? moment(p.irDate).format("MM/DD/YYYY LTS") : "Unknown"}`}
                        >
                            <i className="fa fa-check-circle fa-2x text-success"/>
                        </div>
                    );
                }
                return <div/>;
            },
            total: true,
            totalClasses: "text-center",
            rowTotal: row => {
                // Special checkbox for the instant rebate column
                const p = find(row.providerClinicEnrollments, { providerId: provider.id });
                if (p.isIr) {
                    return 1;
                }
                return 0;
            }
        }
    }

    const LINKS = [{
        name: "Links",
        selector: "links",
        key: "links",
        format: row => (
            <div className="flex">
                {canViewRedemptionSummary && (
                    <Button
                        type="success"
                        iconOnly
                        text
                        onClick={(e) => handleClick(e, row)}
                        title="Clinic Redemption Summary"
                    >
                       <i className="fa fa-ticket margin-right-sm"/>
                    </Button>
                )}
                {canViewClinicPrograms  && (
                    <Button
                        type="success"
                        iconOnly
                        text
                        onClick={(e) => handleGoToClinicPrograms(e, row.clinicId, (providerId || row.territory.providerId))}
                        title="Clinic Programs"
                    >
                        <i className="fa fa-envelope-circle margin-right-x-sm text-success fa-2x" />
                    </Button>
                )}
                {canViewClinicBIGamesDashboard && (
                    <Button
                        type="success"
                        iconOnly
                        text
                        onClick={(e) => handleGoToClinicGames(e, row.clinicId, (providerId || row.territory.providerId))}
                        title="Clinic Games"
                    >
                       <i className="fa fa-trophy-alt margin-right-sm text-success" />
                    </Button>
                )}
                {(columns?.showCouponLibraryLink && canViewCouponLibrary) && (
                    <Button
                        type="success"
                        iconOnly
                        text
                        onClick={(e) => handleGoToCoupons(e, row.clinicId, (providerId || row.territory.providerId))}
                        title="Clinic Coupon Library"
                    >
                       <i className="fa fa-scissors text-success" />
                    </Button>
                )}
            </div>
        ),
    }];

    const MINIMAL_COLUMNS = [
        ...canViewRedemptionSummary || canViewClinicBIGamesDashboard || (columns?.showCouponLibraryLink && canViewCouponLibrary) ? LINKS : [], {

        name: "Clinic",
        selector: "clinicName",
        key: "clinicName",
        sortable: true,
        searchable: true,
        format: row => (
            <div>
                <div className={classnames({
                    "text-primary": (canViewClinicInfo || canViewCorporateGroupClinic)
                })}>
                    {row.clinicName}
                </div>
                <div style={{ fontSize: "0.7em" }}>{row.city}, {row.state}</div>
            </div>
        ),
    }];

    const ACCOUNT_COLUMN = [{
        name: columns?.accountNumberHeader,
        selector: "accountNumber",
        key: "accountNumber",
        sortable: true,
        sortValue: row => toInteger(row.accountNumber),
        searchable: true,
    }];

    const LOCATION_COLUMN = [{
        name: columns?.locationIdHeader,
        selector: "locationId",
        key: "locationId",
        sortable: true,
        sortValue: row => toInteger(row.locationId),
        searchable: true,
    }];

    const handleGoToTerritory = (e, territoryNodeId) => {
        e.stopPropagation();
        e.preventDefault();
        const link = ProviderLinks.getLinkLocation("dashboard", {
            providerId,
            nodeId: territoryNodeId,
            isAdmin,
        });
        history.push(link);
    };

    const TERRITORY_MANAGER_COLUMN = [{
        name: columns?.territoryManagerHeader,
        selector: "territoryManager",
        key: "territoryManager",
        info: "Territory Manager",
        sortable: true,
        searchable: true,
        format: row => {
            const name = get(row, "territory.name", "");
            const territoryNodeId = get(row, "territory.nodeId", null);

            if (isAdmin && providerId === BOEHRINGER_INGELHEIM && territoryNodeId && territoryNodeId !== nodeId) {
                return (
                    <div
                        className="text-primary pointer"
                        onClick={(e) => handleGoToTerritory(e, territoryNodeId)}
                        title={`Go to ${name}`}
                    >
                        {name}
                    </div>
                )
            }

            return (
                <div>{name}</div>
            )
        },
    }];
    const MANUFACTURER_COLUMNS = [];
    if (providerBIEnrollments) {
        MANUFACTURER_COLUMNS.push(getIRManufacturerColumn(providerBIEnrollments));
        MANUFACTURER_COLUMNS.push(getManufacturerColumn(providerBIEnrollments));
    }

    const OTHER_ENROLLED_PROVIDERS_COLUMN = [{
        name: columns?.otherEnrolledProvidersHeader,
        selector: "otherEnrolledProviderIds",
        key: "otherEnrolledProviderIds",
        sortable: true,
        searchable: true,
        format: row => (
            <div className="flex flex-wrap spaced-content">
                {map(row.otherEnrolledProviderIds, id => (
                    <div className={classnames(styles.providers, "flex-1")}>
                        <Image
                            src={providers[id].smallImageUri}
                            fallbackSrc={providers[id].mediumImageUri}
                            alt={providers[id].name}
                        />
                    </div>
                ))}
            </div>
        )
    }];

    const ALL_COLUMNS = [
        ...MINIMAL_COLUMNS,
        ...(columns?.showAccountNumber ? ACCOUNT_COLUMN : []),
        ...(columns?.showLocationId ? LOCATION_COLUMN : []),
        ...(columns?.showTerritoryManager ? TERRITORY_MANAGER_COLUMN : []),
        ...(nodeType === OrgNodeTypes.NHO ? MANUFACTURER_COLUMNS : []),
        ...(!wellnessOnly ? map(columns?.additionalColumns, col => {
            switch(col.id) {
                case "dollarAmountPending":
                    return {
                        name: col.header,
                        selector: col.id,
                        key: col.id,
                        sortable: true,
                        searchable: true,
                        total: true,
                        rowTotal: row => row?.data?.dollarAmountPending || 0,
                        dollarTotal: true,
                        format: row => {
                            const value = row?.[col.id] || 0;
                            return formatUSD(value);
                        }
                    };
                case "ytdRedemptionTotal":
                    return {
                        name: col.header,
                        selector: col.id,
                        key: col.id,
                        sortable: true,
                        searchable: true,
                        total: true,
                        rowTotal: row => row?.data?.ytdRedemptionTotal || 0,
                        dollarTotal: true,
                        format: row => {
                            const value = row?.[col.id] || 0;
                            return formatUSD(value);
                        }
                    };
                case "isOptedIn":
                    return {
                        name: col.header,
                        selector: col.id,
                        key: col.id,
                        sortable: true,
                        searchable: true,
                        total: false,
                        format: row => row?.[col.id] && <i className="fa fa-2x fa-check-circle text-success"/>,
                    }
                case "emailCampaigns":
                    return {
                        name: col.header,
                        selector: col.id,
                        key: col.id,
                        sortable: false,
                        searchable: true,
                        total: false,
                        format: row => row?.[col.id]
                    }
                case "ltoStatus":
                    return {
                        name: col.header,
                        selector: col.id,
                        key: col.id,
                        sortable: false,
                        searchable: true,
                        total: false,
                        format: row => row?.[col.id]
                    }
                default:
                    return {
                        name: col.header,
                        selector: col.id,
                        key: col.id,
                        sortable: true,
                        searchable: true,
                        total: true,
                        format: row => row?.[col.id]
                    }
            }
        }) : []),
        ...(columns?.showOtherEnrolledProviders ? OTHER_ENROLLED_PROVIDERS_COLUMN : [])
    ];

    return (
        <SortableDataTable
            striped
            green
            title={title}
            resultsPerPage={resultsPerPage}
            // downloadPDF={downloadPDF}
            columns={showAllColumns ? ALL_COLUMNS : MINIMAL_COLUMNS}
            rawData={rawData}
            onClick={onRowClick}
            onHover={onRowHover}
            minimalPagination={!showAllColumns}
            allowSearch
            hideSearchBar
            search={search}
            showTotals={!wellnessOnly}
        />
    );
}

ProviderClinicTable.propTypes = {
    columns: PropTypes.object.isRequired,
    rawData: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.array,
    ]),
    resultsPerPage: PropTypes.number,
    showAllColumns: PropTypes.bool,
    onRedemptionsIconClicked: PropTypes.func,
    providerId: PropTypes.number,
    nodeId: PropTypes.number,
    nodeType: PropTypes.string,
    hasGames: PropTypes.bool,
    isAdmin: PropTypes.bool,
    onRowClick: PropTypes.func,
    onRowHoverStart: PropTypes.func,
    onRowHoverEnd: PropTypes.func
}

const connector = connect(
    (state, ownProps) => {
        const userProfile = state.user.userProfile;

        //Permissions
        const canViewCouponLibrary = userHasPermission(PermissionTypes.VIEW, UserPermissions.CLINIC_COUPON_LIBRARY, userProfile);
        const canViewRedemptionSummary = userHasPermission(PermissionTypes.VIEW, UserPermissions.CLINIC_REDEMPTION_SUMMARY, userProfile);
        const canAdminProviderBiGames = userHasPermission(PermissionTypes.ADMIN, UserPermissions.PROVIDER_BI_GAMES, userProfile);
        const canViewProviderBiGames = userHasPermission(PermissionTypes.VIEW, UserPermissions.PROVIDER_BI_GAMES, userProfile);
        const canViewGames = ownProps.hasGames && (canAdminProviderBiGames || canViewProviderBiGames);

        const node = state.entities.nodes[ownProps.nodeId];
        const wellnessOnly = (node?.providerId === PREMIER_PET_CARE_PLAN);

        const canViewClinicInfo = userHasPermission(PermissionTypes.VIEW, UserPermissions.CLINIC_INFO, userProfile);
        const canViewClinicBIGamesDashboard = userHasPermission(PermissionTypes.VIEW, UserPermissions.CLINIC_BI_GAMES_DASHBOARD, userProfile);
        const canViewGamesDashboard = ownProps.hasGames && canViewClinicBIGamesDashboard;
        const canViewClinicWellnessAudit = userHasPermission(PermissionTypes.VIEW, UserPermissions.CLINIC_WELLNESS_AUDIT, userProfile);
        const canViewClinicWellnessPlans = userHasPermission(PermissionTypes.VIEW, UserPermissions.CLINIC_WELLNESS_PLANS, userProfile);
        const canViewClinicWellnessPetPlans = userHasPermission(PermissionTypes.VIEW, UserPermissions.CLINIC_WELLNESS_PET_PLANS, userProfile);
        const canViewCorporateGroupClinic = userHasPermission(PermissionTypes.VIEW, UserPermissions.CORPORATE_GROUP_CLINIC, userProfile);
        const canViewWellnessDvms = userHasPermission(PermissionTypes.VIEW, UserPermissions.CLINIC_WELLNESS_DVMS, userProfile);
        const canEditCouponReprocessing = userHasPermission(PermissionTypes.EDIT, UserPermissions.COUPON_REPROCESSING, userProfile);

        // If User has one of the Wellness Permissions then give them the settings permission
        const canViewWellnessSettings =  canViewClinicWellnessAudit || canViewClinicWellnessPlans || canViewClinicWellnessPetPlans || canViewWellnessDvms || canEditCouponReprocessing;
        const canViewClinicPrograms = userHasPermission(PermissionTypes.VIEW, UserPermissions.PROVIDER_CLINICS_PROGRAMS, userProfile);

        return {
            userProfile,
            providers: state.entities.providers,
            providerBIEnrollments: state.entities.providers?.[BOEHRINGER_INGELHEIM],
            search: state.entities.genericSearch,
            //Permissions
            canViewClinicInfo,
            canViewGamesDashboard,
            canViewCorporateGroupClinic,
            canViewCouponLibrary,
            canViewRedemptionSummary,
            canViewGames,
            wellnessOnly,
            canViewWellnessSettings,
            canViewClinicPrograms,
        };
    },
);

export default compose(
    withRouter,
    connector
) (ProviderClinicTable);
